import type { ReceiptLine, ShiftStatus } from '@tempmee/core';
import { isEmployeeOptionShift } from '~/components/WorkerClassification/utils';
import { CONVENIENCE_FEE_INFO } from '~/constants/billing';
import { type IJob, JobStatusEnum, UserTypeEnum } from '~/models';
import { formatMoney } from '~/utils/formatMoney';
import { feePercent } from '../../shared/utils/shiftPanelsProps';

/** A terminal shift as this page reads it — the route job, or a partial while it loads. */
export type TCancelledShift = Partial<IJob> | null | undefined;

/**
 * Which of the four Figma frames the page is rendering.
 *
 * The ticket describes the split as "status plus pro presence", but those two
 * inputs cannot separate the two frames that *both* show a professional: what
 * distinguishes them is who walked away. So the discriminator is three-part —
 * expired vs cancelled, professional attached or not, and, when one is,
 * whether they were the canceller.
 *
 * `unfilled` covers both frames with no professional (cancelled-no-applicant
 * and expired); they differ only in copy, which {@link toUnfilledDescription}
 * and {@link toPageTitle} derive from the status.
 */
export type TCancelledExpiredVariant =
  | 'unfilled'
  | 'cancelled-by-professional'
  | 'cancelled-with-professional';

/**
 * Whether the shift lapsed rather than being cancelled.
 *
 * Only `expired` qualifies. The three cancelled statuses (`cancelled`,
 * `cancelled-hyg`, `cancelled-dnt`) all describe a shift somebody ended, and
 * this page treats them alike — the *who* comes from
 * {@link wasCancelledByProfessional}, which reads the field that actually
 * records it rather than inferring from the status suffix.
 */
export const isExpiredShift = (job: TCancelledShift): boolean =>
  job?.status === JobStatusEnum.Expired;

/**
 * Shift → the `ShiftStatus` the summary block takes.
 *
 * A normaliser, not a status table: it maps this page's four `JobStatusEnum`
 * values onto the two design-system states, and `SHIFT_STATUS_META` in
 * `@tempmee/core` still owns every label and colour behind them.
 */
export const toShiftStatus = (job: TCancelledShift): ShiftStatus =>
  isExpiredShift(job) ? 'expired' : 'cancelled';

/** The page's `<h1>`, per the Figma frames. */
export const toPageTitle = (job: TCancelledShift): string =>
  isExpiredShift(job) ? 'Expired Shift' : 'Cancelled Shift';

/**
 * Whether the professional cancelled, rather than the office or GoTu.
 *
 * `HYG` is the professional side of `is_cancelled_by_user_type` (`DNT` is the
 * office). This is the same test the legacy screen makes, and it drives two
 * things there: the "re-posted" notice, and the suppression of the whole
 * receipt/transaction-history block — because a professional cancelling costs
 * the office nothing to show.
 *
 * Note it is independent of `cancelPerformedBy`, which records the *account*
 * that performed the operation and is frequently GoTu support acting for the
 * professional.
 */
export const wasCancelledByProfessional = (job: TCancelledShift): boolean =>
  job?.is_cancelled_by_user_type === UserTypeEnum.HYG;

/**
 * The backend's time rule for a repost: it compares the moment of cancellation
 * with the shift's end. Only a cancellation provably at or after the end rules
 * the repost out; a missing or unreadable timestamp keeps the notice, which is
 * what the page showed before the rule was read.
 */
const wasCancelledBeforeShiftEnd = (job: TCancelledShift): boolean => {
  const cancelledAt = new Date(job?.job_cancelled_datetime ?? NaN).getTime();
  const shiftEnd = new Date(job?.job_datetime_end ?? NaN).getTime();

  if (Number.isNaN(cancelledAt) || Number.isNaN(shiftEnd)) {
    return true;
  }

  return cancelledAt < shiftEnd;
};

/**
 * Whether the shift was reposted after the professional cancelled.
 *
 * **There is no field for this**, and the two that look like one are a trap:
 * `repost` and `repostCount` describe the job you are *looking at* — whether it
 * was itself born from a repost — not whether it spawned one. The backend
 * stamps them on the **new** job (`repostJob`: `repost: true`,
 * `repostCount: (job.repostCount || 0) + 1`, "initial job has repostCount of
 * 0") and never writes back to the cancelled original, which is why every
 * professional-cancelled shift on dev carries neither.
 *
 * So this is derived from the conditions under which the backend even attempts
 * the repost. `handleHygienistCancellation` reposts only when the professional
 * cancelled, the shift is neither an onsite interview nor a dual-model shift,
 * *and* the cancellation came before the shift's end — a shift that is already
 * over has nothing left to fill. The payload exposes all of these.
 *
 * Two known limits, both raised rather than papered over:
 *
 * - This reads "a repost was attempted", not "a repost exists". `repostJob` can
 *   throw, and the backend handles that by refunding the office in full and
 *   logging to Sentry — nothing reaches the frontend, so a failed repost still
 *   shows the notice. Closing that needs a field on the cancelled job.
 * - The legacy screen shows the notice for *every* professional cancellation,
 *   interviews included, wording it "This interview has been re-posted". The
 *   backend never reposts an interview, so that copy is simply wrong; it is not
 *   reproduced here.
 */
export const wasRepostedAfterCancellation = (job: TCancelledShift): boolean =>
  wasCancelledByProfessional(job) &&
  !job?.isOnsiteInterview &&
  !job?.dualModelEnabled &&
  wasCancelledBeforeShiftEnd(job);

/** The professional who was booked on the shift, if it ever got one. */
export const toShiftProfessional = (job: TCancelledShift) => job?.offer_final_reciever;

/**
 * Shift → which frame to render.
 *
 * Presence of a professional is read from `offer_final_reciever` — the field the
 * legacy screen gates its own professional rows on — rather than from the
 * applicant list: an expired shift can have had invitees and even applicants,
 * and neither means somebody was booked.
 */
export const resolveCancelledExpiredVariant = (job: TCancelledShift): TCancelledExpiredVariant => {
  if (!toShiftProfessional(job)) {
    return 'unfilled';
  }

  return wasCancelledByProfessional(job)
    ? 'cancelled-by-professional'
    : 'cancelled-with-professional';
};

/**
 * Subtitle of the "Shift Unfilled" state, worded per status as QA specified on
 * GOTU-13265: an expired shift lapsed because nobody was confirmed in time, and
 * a cancelled one ended before anybody was. Applicants are not the measure — a
 * shift can have had applicants and still never been confirmed.
 */
export const toUnfilledDescription = (job: TCancelledShift): string =>
  isExpiredShift(job)
    ? 'No professionals were confirmed before the time of the shift.'
    : 'The shift was cancelled before a professional was confirmed.';

/**
 * Whether the page shows a Receipt at all.
 *
 * Legacy parity: the money block is rendered for a cancelled shift that had a
 * professional, and suppressed entirely when that professional was the one who
 * cancelled. The Figma agrees — its cancelled-by-pro frame has no Receipt — so
 * both sources point the same way.
 *
 * An expired shift never had a booking, so there is nothing to charge or refund.
 */
export const hasCancellationReceipt = (job: TCancelledShift): boolean =>
  Boolean(toShiftProfessional(job)) && !isExpiredShift(job) && !wasCancelledByProfessional(job);

/**
 * Shift → the Receipt lines for a cancellation.
 *
 * Deliberately *not* `toReceipt` from the shared panels view: that one bills a
 * shift that happened (regular and overtime pay against `total`), and a
 * cancelled shift is billed on a different basis — the cancellation fee plus
 * the fees that survive it. Reusing it would have shown the office the full
 * shift cost for a shift nobody worked.
 *
 * The rows are the legacy cancelled panel's, in its order: the cancellation fee,
 * then the shared service/employee/convenience fees. Percentages come from the
 * shift's own `*_percent` fields, as legacy reads them.
 */
export const toCancellationReceiptLines = (job: TCancelledShift): ReceiptLine[] => {
  const lines: ReceiptLine[] = [
    { label: 'Cancellation Fee', value: formatMoney(job?.cancellation_fee) },
    {
      label: `Service Fee${feePercent(null, job?.tm_fee_percent)}`,
      value: formatMoney(job?.tm_fee),
    },
  ];

  if (isEmployeeOptionShift(job)) {
    lines.push({ label: 'Employee Fee', value: formatMoney(job?.employeeOptionFee) });
  }

  if (job?.convenience_fee_percent) {
    lines.push({
      label: `Convenience Fee${feePercent(null, job?.convenience_fee_percent)}`,
      value: formatMoney(job?.convenience_fee),
      info: CONVENIENCE_FEE_INFO,
    });
  }

  return lines;
};

/**
 * The Receipt's total — the amount refunded to the office.
 *
 * The backend's `total_refund`, never a local sum: the office was refunded what
 * the server computed, and re-adding the lines here would let the two drift.
 *
 * Legacy labelled this row "Refund Total {card} *{last four}". The block's total
 * label is fixed at "Total" and takes no override, so the card detail has no
 * home on this page — flagged for the design system rather than bolted on as a
 * line that would read like another charge.
 */
export const toCancellationRefundTotal = (job: TCancelledShift): string =>
  formatMoney(job?.total_refund);
